import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import type { ReKeyStatus } from './useGetOrderDetail';
import type { AdditionalFilters } from './useHomeFeed';

export type Phase = 'new-order' | 'in-quoting' | 'ready-to-order' | 'ordered' | 'rejected';
export interface PhaseEntry {
    phase: Phase;
    total: number;
    alert?: string;
}

export type NewOrderStatus = 'first-quote' | 'additional-parts';
export type RekeyingStatus = 'needs-export' | 'needs-rekey';
export type QuotingStatus = 'in-quoting' | 'extended-quoting';
export type ReadyToOrderStatus = 'confirm-selections' | 'below-margin-threshold' | 'check-flags' | 'parts-not-quoted' | 'potential-total' | 'quotes-only' | 'auto-ordered-stopped';
export type OrderedStatus = 'partially-ordered' | 'all-ordered' | 'rejected' | 'confirmed' | 'not-confirmed';
export interface Action {
    key: NewOrderStatus | RekeyingStatus | ReadyToOrderStatus | OrderedStatus | QuotingStatus;
    total?: number;
    exclamation?: string;
    flag?: string;
}

interface AllOrdersEntry {
    _id: string;
    type: 'all',
    roNumber: string;
    vin: string;
    ymm: string;
    firstName: string | null;
    lastName: string | null;
    partsTotal: number;
    partsOrdered: number;
    phases: Array<PhaseEntry>;
    canSubmitQuote: boolean;
    canPlaceOrder: boolean;
    daysOpen: string;
    actionRequired: boolean;
    canComplete: boolean;
    locationName: string | null;
    noteCount: number;
    supplierCommunicationCount: number;
    canEnterEstimate: boolean;
}

interface NewOrderEntry {
    _id: string;
    type: 'new-order',
    roNumber: string;
    vin: string;
    ymm: string;
    status: Action;
    firstName: string | null;
    lastName: string | null;
    partsToQuote: number;
    totalParts: number;
    partsOrdered: number;
    daysOpen: string;
    timeExported: Date;
    canComplete: boolean;
    locationName: string | null;
    noteCount: number;
    supplierCommunicationCount: number;
}

interface QuotingEntry {
    _id: string;
    type: 'in-quoting';
    roNumber: string;
    vin: string;
    ymm: string;
    status: Action;
    firstName: string | null;
    lastName: string | null;
    partsQuoting: number;
    daysOpen: string;
    canComplete: boolean;
    locationName: string | null;
    estQuoteReturnTime: Date | null;
    estQuoteReturnScheduledTime: Date | null;
    noteCount: number;
    canEnterEstimate: boolean;
    supplierCommunicationCount: number;
}

interface ReadyToOrderEntry {
    _id: string;
    type: 'ready-to-order';
    roNumber: string;
    vin: string;
    ymm: string;
    firstName: string | null;
    lastName: string | null;
    totalParts: number;
    partsOrdered: number;
    status: Array<Action>;
    orderValue: number;
    grossProfitMargin: number;
    deliveryTime: string;
    daysOpen: string;
    canComplete: boolean;
    locationName: string | null;
    noteCount: number;
    canEnterEstimate: boolean;
    supplierCommunicationCount: number;
}

interface OrderedEntry {
    _id: string;
    type: 'ordered';
    roNumber: string;
    vin: string;
    ymm: string;
    firstName: string | null;
    lastName: string | null;
    totalParts: number;
    partsOrdered: number;
    orderValue: number;
    grossProfitMargin: number;
    status: Array<Action>;
    eta: string;
    readyToOrderParts: number;
    canComplete: boolean;
    locationName: string | null;
    timeOrdered: string | null;
    noteCount: number;
    supplierCommunicationCount: number;
}

interface CompletedEntry {
    _id: string;
    type: 'completed';
    roNumber: string;
    vin: string;
    ymm: string;
    firstName: string | null;
    lastName: string | null;
    totalParts: number;
    partsOrdered: number;
    orderValue: number;
    grossProfitMargin: number;
    timeCompleted: Date | null;
    locationName: string | null;
    noteCount: number;
    supplierCommunicationCount: number;
}

interface ReKeyingEntry {
    _id: string;
    type: 're-keying';
    roNumber: string;
    firstName: string | null;
    lastName: string | null;
    vin: string;
    ymm: string;
    timeOrdered: string | null;
    partsConfirmed: number;
    insurance: string | null;
    partsReKeyed: number;
    status: ReKeyStatus;
    canComplete: boolean;
    locationName: string | null;
    canEnterEstimate: boolean;
    noteCount: number;
    supplierCommunicationCount: number;
}

export type OrderFeedEntry = AllOrdersEntry | NewOrderEntry | QuotingEntry | ReadyToOrderEntry | OrderedEntry | CompletedEntry | ReKeyingEntry;

export type {
    AllOrdersEntry,
    NewOrderEntry,
    QuotingEntry,
    ReadyToOrderEntry,
    OrderedEntry,
    CompletedEntry,
    ReKeyingEntry
};

interface OrderFeedResponse {
    orders: Array<OrderFeedEntry>;
    numRejected: number;
}

interface OrdersFeedInput {
    type: Phase | 'all' | 'archived' | 'completed' | 're-keying';
    limit: number;
    offset: number;
    locationIds: Array<string> | 'all';
    search?: string;
    additionalFilters?: AdditionalFilters;
}

const getOrdersFeedPaginated = async (
    sessionId: string,
    input: OrdersFeedInput
): Promise<OrderFeedResponse> => {
    const { data } = await axios.post(`${API_URL}/orders/feed`,
        input,
        {
            headers: {
                Authorization: sessionId
            }
        }
    );
    return data;
};

export const useGetOrdersFeedPaginated = (
    type: Phase | 'all' | 'archived' | 'completed' | 're-keying',
    locationIds: Array<string> | 'all',
    page: number = 1,
    limit: number = 20,
    search: string = '',
    additionalFilters: AdditionalFilters = {}
) => {
    const sessionContext = useContext(SessionContext);
    const offset = (page - 1) * limit;

    return useQuery({
        queryKey: ['/dashboard/orders-feed-paginated', type, locationIds === 'all' ? 'all' : locationIds.join(','), page, limit, search, JSON.stringify(additionalFilters)],
        queryFn: () =>
            getOrdersFeedPaginated(sessionContext?.sessionId ?? '', {
                type,
                locationIds,
                limit,
                offset,
                search,
                additionalFilters: Object.keys(additionalFilters).length > 0 ? additionalFilters : undefined
            }),
        enabled: !!sessionContext?.sessionId && (locationIds === 'all' || locationIds.length > 0),
        refetchInterval: 1000 * 60,
    });
};
